import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  Form,
  Button,
  Segment,
  Header,
  Icon,
  Message,
  Loader,
  Checkbox,
} from "semantic-ui-react";
import {
  SG_ADD_STATUS_ORDER,
  SG_EDIT_STATUS_ORDER,
  SG_FETCH_STATUS_ORDER,
} from "constants/actions";

export default function StatusForm() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const { status_order, pending, error, message } = useSelector(
    (state) => state.action_planning_status || {}
  );

  const [formData, setFormData] = useState({
    table_name: "",
    status_name: "",
    display_name: "",
    description: "",
    order: 0,
    is_active: true,
    is_terminal: false,
    is_error: false,
  });

  useEffect(() => {
    if (isEdit && id) {
      dispatch({
        type: SG_FETCH_STATUS_ORDER,
        payload: { id },
      });
    }
  }, [dispatch, id, isEdit]);

  useEffect(() => {
    if (isEdit && status_order) {
      setFormData({
        table_name: status_order.table_name || "",
        status_name: status_order.status_name || "",
        display_name: status_order.display_name || "",
        description: status_order.description || "",
        order: status_order.order || 0,
        is_active: status_order.is_active !== false,
        is_terminal: status_order.is_terminal || false,
        is_error: status_order.is_error || false,
      });
    }
  }, [status_order, isEdit]);

  const handleChange = (e, { name, value, checked, type }) => {
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleSubmit = () => {
    if (isEdit) {
      dispatch({
        type: SG_EDIT_STATUS_ORDER,
        payload: {
          id,
          data: formData,
        },
      });
    } else {
      dispatch({
        type: SG_ADD_STATUS_ORDER,
        payload: formData,
      });
    }
    // Navigate back after a short delay to allow the action to complete
    setTimeout(() => {
      navigate("/app/action/status/list");
    }, 1000);
  };

  const handleCancel = () => {
    navigate("/app/action/status/list");
  };

  const tableOptions = [
    { key: "action", text: "Action", value: "action" },
    { key: "campaign", text: "Campaign", value: "campaign" },
    { key: "event", text: "Event", value: "event" },
    { key: "report", text: "Report", value: "report" },
    { key: "response", text: "Response", value: "response" },
    { key: "email", text: "Email", value: "email" },
  ];

  const iconOptions = [
    { key: "circle", text: "Circle", value: "circle" },
    { key: "check circle", text: "Check Circle", value: "check circle" },
    { key: "times circle", text: "Times Circle", value: "times circle" },
    { key: "dot circle", text: "Dot Circle", value: "dot circle" },
    { key: "play circle", text: "Play Circle", value: "play circle" },
    { key: "pause circle", text: "Pause Circle", value: "pause circle" },
    { key: "stop circle", text: "Stop Circle", value: "stop circle" },
  ];

  if (isEdit && pending && !status_order) {
    return (
      <Loader active inline="centered">
        Loading status order...
      </Loader>
    );
  }

  return (
    <Segment>
      <Header as="h2">
        <Icon name={isEdit ? "edit" : "plus"} />
        <Header.Content>
          {isEdit ? "Edit Status Order" : "Create Status Order"}
          <Header.Subheader>
            {isEdit
              ? "Update the status order configuration"
              : "Add a new status order configuration"}
          </Header.Subheader>
        </Header.Content>
      </Header>

      {error && (
        <Message negative>
          <Message.Header>Error</Message.Header>
          <p>{error}</p>
        </Message>
      )}

      {message && (
        <Message positive>
          <Message.Header>Success</Message.Header>
          <p>{message}</p>
        </Message>
      )}

      <Form onSubmit={handleSubmit} loading={pending}>
        <Form.Select
          label="Table Name"
          name="table_name"
          placeholder="Select table"
          options={tableOptions}
          value={formData.table_name}
          onChange={handleChange}
          required
        />

        <Form.Input
          label="Status Name (Code)"
          name="status_name"
          placeholder="e.g., pending, in_progress, completed"
          value={formData.status_name}
          onChange={handleChange}
          required
        />

        <Form.Input
          label="Display Name"
          name="display_name"
          placeholder="e.g., Pending, In Progress, Completed"
          value={formData.display_name}
          onChange={handleChange}
          required
        />

        <Form.TextArea
          label="Description"
          name="description"
          placeholder="Optional description of this status"
          value={formData.description}
          onChange={handleChange}
        />

        <Form.Input
          label="Order"
          name="order"
          type="number"
          placeholder="Display order (lower numbers appear first)"
          value={formData.order}
          onChange={handleChange}
          required
        />

        <Form.Field>
          <Checkbox
            label="Active"
            name="is_active"
            checked={formData.is_active}
            onChange={handleChange}
          />
        </Form.Field>

        <Form.Field>
          <Checkbox
            label="Terminal Status (end state)"
            name="is_terminal"
            checked={formData.is_terminal}
            onChange={handleChange}
          />
        </Form.Field>

        <Form.Field>
          <Checkbox
            label="Error Status"
            name="is_error"
            checked={formData.is_error}
            onChange={handleChange}
          />
        </Form.Field>

        <Button type="submit" primary loading={pending}>
          <Icon name="save" />
          {isEdit ? "Update" : "Create"}
        </Button>
        <Button type="button" onClick={handleCancel}>
          <Icon name="cancel" />
          Cancel
        </Button>
      </Form>
    </Segment>
  );
}
